import 'src/styles/ui/modal.css';

import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ReactModal from 'react-modal';
import config from 'src/utils/config';

if (!config.test) ReactModal.setAppElement('#app');

const Modal = ({
  isOpen,
  onConfirm,
  onDismiss,
  onAdditionalAction,
  confirmText,
  dismissText,
  additionalActionText,
  confirmClass,
  title,
  subTitle,
  children,
  scrollable,
  onAfterOpen,
  dataCy,
}) => {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    await onConfirm();
    setConfirming(false);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      className={{
        base: 'modal fade d-block',
        afterOpen: 'show',
        beforeClose: 'before-close',
      }}
      onRequestClose={onDismiss}
      onAfterOpen={onAfterOpen}
    >
      <div
        className={`modal-dialog modal-dialog-centered ${scrollable ? 'modal-dialog-scrollable' : ''}`}
        role='document'
        data-cy={dataCy}
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <div className='w-100'>
              <div className='d-flex mb-1'>
                <h5 className='modal-title' data-cy={dataCy && `${dataCy}-title`}>
                  {title}
                </h5>
                <button
                  type='button'
                  className='close'
                  aria-label='Close'
                  data-cy={dataCy && `${dataCy}-close-btn`}
                  onClick={onDismiss}
                >
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
              {subTitle && subTitle}
            </div>
          </div>
          <div className='modal-body'>{children}</div>
          {(confirmText || dismissText) && (
            <div className='modal-footer'>
              {dismissText && (
                <button
                  type='button'
                  className='button button--sm'
                  disabled={confirming}
                  data-cy={dataCy && `${dataCy}-dismiss-btn`}
                  onClick={onDismiss}
                >
                  {dismissText}
                </button>
              )}
              {additionalActionText && (
                <button
                  type='button'
                  className='button button--sm button-primary'
                  disabled={confirming}
                  data-cy={dataCy && `${dataCy}-additional-btn`}
                  onClick={onAdditionalAction}
                >
                  {additionalActionText}
                </button>
              )}
              {confirmText && (
                <button
                  type='button'
                  className={`button button--sm ${confirmClass} position-relative`}
                  disabled={confirming}
                  data-cy={dataCy && `${dataCy}-confirm-btn`}
                  onClick={handleConfirm}
                >
                  <div
                    className={`modal-spinner ${confirming ? 'd-flex' : 'd-none'} align-items-center justify-content-center`}
                  >
                    <div
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    />
                  </div>
                  <span className={`${confirming ? 'invisible' : 'visible'}`}>{confirmText}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </ReactModal>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  confirmText: PropTypes.string,
  dismissText: PropTypes.string,
  confirmClass: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  scrollable: PropTypes.bool,
  onAfterOpen: PropTypes.func,
  additionalActionText: PropTypes.string,
  onAdditionalAction: PropTypes.func,
  dataCy: PropTypes.string,
};

Modal.defaultProps = {
  confirmText: null,
  dismissText: null,
  confirmClass: 'button--primary',
  scrollable: false,
  onAfterOpen: null,
  additionalActionText: null,
  onAdditionalAction: null,
  dataCy: null,
};

export default Modal;
